import type { Entry } from "contentful";
import { useEffect, useMemo, useState } from "react";

import type { TypeTextBlockSkeleton } from "~/contentful/compiled";
import type { SerializeFrom } from "~/lib/types";
import { useRootLayoutData } from "~/root";

import * as Nav from "./meganav-nav";

interface TopBarMessagesSliderProps {
  data?: SerializeFrom<Entry<
    TypeTextBlockSkeleton,
    "WITHOUT_UNRESOLVABLE_LINKS",
    string
  > | null>;
}

type MarkType = "bold" | "italic" | "underline";

// Mapping from marks to Tailwind classes
const markToClass: Record<MarkType, string> = {
  bold: "font-bold",
  italic: "italic",
  underline: "underline",
};

function isMarkType(type: string): type is MarkType {
  return type in markToClass;
}

export default function TopBarMessagesSlider({
  data,
}: TopBarMessagesSliderProps) {
  const [currentPromoMessage, setCurrentPromoIndex] = useState(0);
  const messages = useMemo(() => data?.fields?.blockContent?.content, [data]);
  const { TOP_BAR_MESSAGES_INTERVAL } = useRootLayoutData();

  useEffect(() => {
    if (messages && messages.length > 1) {
      const interval = setInterval(() => {
        setCurrentPromoIndex(prevIndex => (prevIndex + 1) % messages.length);
      }, TOP_BAR_MESSAGES_INTERVAL || 3000);

      return () => clearInterval(interval);
    }
  }, [messages?.length]);

  if (messages?.length === 0) {
    return null;
  }

  const currentMessage = messages ? messages[currentPromoMessage] : null;

  return (
    <>
      {currentMessage ? (
        <Nav.PromoHeading className="relative z-50 place-content-around items-center bg-message text-xs text-white">
          <span>
            {currentMessage.content.map((item, index) => {
              if (item.nodeType !== "text" || !item.value) return null;

              const itemClasses = item.marks
                ?.map(mark =>
                  isMarkType(mark.type) ? markToClass[mark.type] : "",
                )
                .join(" ");

              return (
                <span key={index} className={`${itemClasses}`}>
                  {item.value}
                </span>
              );
            })}
          </span>
        </Nav.PromoHeading>
      ) : null}
    </>
  );
}
